import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LANG_MAP = { fi: "finnish", en: "english", fr: "french", de: "german", es: "spanish" };

export default function FreeTrial() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = LANG_MAP[i18n.language?.substring(0, 2)] || "english";
  const [dream, setDream] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [used, setUsed] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.is_admin) return false;
      return localStorage.getItem("uneksia_trial_used") === "true";
    }
    catch { return false; }
  });

  const handleAnalyze = async () => {
    if (dream.trim().length < 10 || loading || used) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/api/dreams/try-analyze`, {
        content: dream.trim(),
        language: lang,
      }, { headers });
      if (res.data.preview) {
        setPreview(res.data.preview);
      }
      if (!res.data.limited) {
        localStorage.setItem("uneksia_trial_used", "true");
      }
      setUsed(true);
    } catch {
      setUsed(true);
    } finally {
      setLoading(false);
    }
  };

  // State: has preview text to show
  const renderResult = () => (
    <div className="space-y-6">
      <div className="bg-white/70 rounded-2xl p-6 border border-purple-100/50">
        <div className="prose prose-sm max-w-none font-body text-void/80 whitespace-pre-wrap">
          {preview}
        </div>
      </div>
      <div className="relative">
        <div className="bg-gradient-to-b from-white/70 to-white/30 rounded-2xl p-6 border border-purple-100/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 py-4">
            <Lock className="w-8 h-8 text-purple-400" />
            <p className="font-heading text-void text-lg text-center">
              {t("trial.registerToSeeMore")}
            </p>
            <p className="text-void/50 font-body text-sm text-center max-w-md">
              {t("trial.registerDescription")}
            </p>
            <Button
              onClick={() => navigate("/auth")}
              className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-3 font-body font-medium transition-all duration-300 hover:scale-105 mt-2"
              data-testid="free-trial-register-btn"
            >
              <span className="flex items-center gap-2">
                {t("trial.registerFree")}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // State: already used, no preview
  const renderUsed = () => (
    <div className="flex flex-col items-center gap-3 py-4">
      <Lock className="w-8 h-8 text-purple-400" />
      <p className="font-heading text-void text-lg text-center">
        {t("trial.registerToSeeMore")}
      </p>
      <p className="text-void/50 font-body text-sm text-center max-w-md">
        {t("trial.registerDescription")}
      </p>
      <Button
        onClick={() => navigate("/auth")}
        className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-3 font-body font-medium transition-all duration-300 hover:scale-105 mt-2"
        data-testid="free-trial-register-btn"
      >
        <span className="flex items-center gap-2">
          {t("trial.registerFree")}
          <ArrowRight className="w-4 h-4" />
        </span>
      </Button>
    </div>
  );

  // State: input form
  const renderForm = () => (
    <div className="space-y-4">
      <textarea
        value={dream}
        onChange={(e) => setDream(e.target.value)}
        placeholder={t("trial.placeholder")}
        className="w-full h-36 p-4 rounded-2xl border border-purple-100 bg-white/60 font-body text-void text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all"
        maxLength={2000}
        disabled={loading}
        data-testid="free-trial-input"
      />
      <div className="text-center">
        <Button
          onClick={handleAnalyze}
          disabled={dream.trim().length < 10 || loading}
          className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-3 font-body font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50"
          data-testid="free-trial-analyze-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              {t("trial.analyzing")}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {t("trial.analyze")}
            </span>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <section id="free-trial" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="free-trial-section">
      <div className="glass-effect rounded-3xl p-8 sm:p-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-body text-purple-700">{t("trial.badge")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-light text-void mb-2">
            {t("trial.title")}
          </h2>
          <p className="text-void/60 font-body">
            {t("trial.subtitle")}
          </p>
        </div>

        {preview ? renderResult() : used ? renderUsed() : renderForm()}
      </div>
    </section>
  );
}
